/**
 * Bond Mentor — Date & Time Utilities
 * Format: MM-DD-YYYY | 12-hour (non-military) time
 */

/**
 * Accepts Date objects, ISO strings, timestamps, and safe date-like values.
 * Older versions only accepted Date objects, so stored history strings displayed
 * as the current time. This parser fixes duplicate-looking history rows.
 */
export function toValidDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  // Treat plain date keys as LOCAL dates. JavaScript otherwise parses
  // YYYY-MM-DD as UTC, which can display as the previous day in Eastern time.
  // This also normalizes older UI/display strings like MM-DD-YY into MM-DD-YYYY.
  if (typeof value === 'string') {
    const trimmed = value.trim();

    const isoDateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnlyMatch) {
      const [, year, month, day] = isoDateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const slashDateOnlyMatch = trimmed.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (slashDateOnlyMatch) {
      const [, year, month, day] = slashDateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const usDateMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2}|\d{4})$/);
    if (usDateMatch) {
      const [, month, day, rawYear] = usDateMatch;
      const year = rawYear.length === 2 ? 2000 + Number(rawYear) : Number(rawYear);
      return new Date(year, Number(month) - 1, Number(day));
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return new Date();
}

/**
 * Format a Date object/string to MM-DD-YYYY
 */
export function formatDate(date, preferredFormat = null) {
  const safeDate = toValidDate(date);
  const month = String(safeDate.getMonth() + 1).padStart(2, '0');
  const day = String(safeDate.getDate()).padStart(2, '0');
  const year = safeDate.getFullYear();
  const monthName = safeDate.toLocaleString('en-US', { month: 'long' });
  const format = preferredFormat || (typeof document !== 'undefined' ? document.documentElement.dataset.appDateFormat : '') || 'MM-DD-YYYY';

  if (format === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
  if (format === 'Month Day, Year') return `${monthName} ${Number(day)}, ${year}`;
  return `${month}-${day}-${year}`;
}

/**
 * Format a Date object/string to 12-hour non-military time (e.g., 2:35 PM)
 */
export function formatTime(date, preferredFormat = null) {
  const safeDate = toValidDate(date);
  const minutes = String(safeDate.getMinutes()).padStart(2, '0');
  const format = preferredFormat || (typeof document !== 'undefined' ? document.documentElement.dataset.timeFormat : '') || '12h';

  if (format === '24h') {
    const hours24 = String(safeDate.getHours()).padStart(2, '0');
    return `${hours24}:${minutes}`;
  }

  let hours = safeDate.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Format a Date object/string to "MM-DD-YYYY at H:MM AM/PM"
 */
export function formatDateTime(date) {
  return `${formatDate(date)} at ${formatTime(date)}`;
}

/**
 * Get a relative time string, e.g., "5 minutes ago", "2 hours ago"
 */
export function timeAgo(date) {
  const safeDate = toValidDate(date);
  const seconds = Math.floor((new Date() - safeDate) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m} minute${m > 1 ? 's' : ''} ago`;
  }
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    return `${h} hour${h > 1 ? 's' : ''} ago`;
  }
  const d = Math.floor(seconds / 86400);
  return `${d} day${d > 1 ? 's' : ''}`;
}

/**
 * Get greeting based on time of day
 */
export function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}


/**
 * Format a stored snapshot key (YYYY-MM-DD) into MM-DD-YYYY for display.
 * Use this for all user-facing snapshot/replay/coverage labels.
 */
export function formatSnapshotDate(value) {
  if (!value) return '—';
  return formatDate(value);
}

/**
 * Format a date range using the global user-facing MM-DD-YYYY standard.
 */
export function formatDateRange(start, end) {
  const left = start ? formatSnapshotDate(start) : '—';
  const right = end ? formatSnapshotDate(end) : '—';
  return `${left} → ${right}`;
}
